/**
 * Waiting for a transaction the wallet sent, or the venue sent for the user.
 *
 * **Asked for directly, once a second, and never only on a new block.**
 * viem's `waitForTransactionReceipt` looks for the receipt each time the
 * block number moves, and the block number it watches is the wallet's — which
 * MetaMask caches. Measured against a local Anvil mining a block a second
 * (2026-10-07), MetaMask's `eth_blockNumber` sat 14 blocks behind the node and
 * moved twice in thirty seconds, so a withdrawal's countdown, and its removal
 * on "Put it back", appeared 5–10 s after the block that decided it. The same
 * wallet answered `eth_getTransactionReceipt` within 10 ms of the node, and
 * moved its own head to the receipt's block as it did — which is also what
 * makes the re-read straight after this one read the new state.
 *
 * viem's wait still runs beside the direct ask, because it is what follows a
 * transaction the user sped up or cancelled in the wallet: that one mines
 * under a different hash, which asking for this one never finds.
 *
 * Lazily reached, like everything that imports `wagmi/actions` (invariant 13).
 */

import type { WalletConfig } from './connectors'

/** How long to wait for a receipt before re-reading the chain instead. */
export const RECEIPT_TIMEOUT_MS = 30_000

/**
 * How often the receipt is asked for. A second rather than Arbitrum's quarter:
 * every ask is a request from the user's wallet to its RPC provider, so this
 * bounds a wait at thirty of them (spec owner, 2026-10-07).
 */
export const RECEIPT_POLL_MS = 1_000

/**
 * Wait for a sent transaction to be mined, say so if it reverted, and return
 * the block it was mined in — the block to re-read at.
 *
 * `writeContract` returns on **submission**, so a re-read straight after it
 * reads the chain before the transaction is in a block: an `approve` the user
 * had just confirmed still read as the old allowance, the panel kept offering
 * **Allow**, and only a reload showed **Wrap**. Waiting for the receipt is what
 * makes the re-read after it mean something.
 */
export async function untilMined(config: WalletConfig, hash: `0x${string}`): Promise<bigint> {
  const { getTransactionReceipt, waitForTransactionReceipt } = await import('wagmi/actions')
  // **Bounded.** A wallet can hand back a hash and then lose track of it — a
  // MetaMask whose history predates a local chain's reset does exactly that —
  // and an unbounded wait left the setup modal spinning until a reload, over
  // transactions that had in fact mined. On timeout both callers re-read the
  // chain, which is what decides whether the step is done.
  const watched = waitForTransactionReceipt(config, { hash, timeout: RECEIPT_TIMEOUT_MS })
  let settled = false
  const asked = (async () => {
    while (!settled) {
      const receipt = await getTransactionReceipt(config, { hash }).catch(() => null)
      if (receipt) return receipt
      await new Promise((resolve) => globalThis.setTimeout(resolve, RECEIPT_POLL_MS))
    }
    // Only reached once `watched` has already decided the race.
    return watched
  })()
  const receipt = await Promise.race([watched, asked])
    .catch((cause: unknown) => {
      if (cause instanceof Error && /timed? ?out/i.test(`${cause.name} ${cause.message}`)) {
        throw new Error(
          'Your wallet has not confirmed the transaction yet. If it went through, this updates on its own.',
        )
      }
      throw cause
    })
    .finally(() => {
      settled = true
    })
  if (receipt.status === 'reverted') {
    throw new Error('The transaction was sent but reverted on chain. Nothing changed.')
  }
  return receipt.blockNumber
}
